import React, { useEffect, useRef } from "react";
import Questionnaire from "./Questionnaire";
import SharedLayout from "./SharedLayout";
import "../styles/PageWrappers.css";
import scrollToTop from "../utils/scrollToTop";

const QuestionnaireView = ({ user, onLoginClick }) => {
  const mountedRef = useRef(false);

  // Mobile-specific detection
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Check for URL parameters that indicate this is a fresh window on mobile
  const location = window.location;
  const params = new URLSearchParams(location.search);
  const isMobileNewWindow =
    params.get("mobile") === "true" && params.get("new") === "true";

  // Force scroll to top when component mounts, with multiple methods for redundancy
  useEffect(() => {
    // Even more aggressive for mobile new window
    if (isMobileNewWindow) {
      console.log("Mobile new window detected - using aggressive scroll");

      // Immediate scroll attempts
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;

      // Prevent any scroll restoration
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      // Force scroll multiple times with increasing delays
      const scrollIntervals = [
        10, 50, 100, 300, 500, 800, 1000, 1500, 2000, 3000,
      ];

      scrollIntervals.forEach((delay) => {
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }, delay);
      });

      // Add special class for mobile new window
      document.documentElement.classList.add("force-top-scroll");
      document.body.classList.add("force-top-scroll");

      return () => {
        document.documentElement.classList.remove("force-top-scroll");
        document.body.classList.remove("force-top-scroll");
      };
    }

    // Initial scroll attempts
    scrollToTop();
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // More aggressive approach for mobile
    if (isMobile) {
      // Force scroll multiple times with increasing delays
      const scrollIntervals = [10, 50, 100, 300, 500, 1000, 2000];

      scrollIntervals.forEach((delay) => {
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }, delay);
      });

      // Try to prevent any native scroll restoration
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } else {
      // For desktop, fewer attempts are needed
      const scrollTimeout = setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);

      const secondScrollTimeout = setTimeout(() => {
        window.scrollTo(0, 0);
        mountedRef.current = true;
      }, 500);

      return () => {
        clearTimeout(scrollTimeout);
        clearTimeout(secondScrollTimeout);
      };
    }
  }, [isMobile, isMobileNewWindow]);

  // Add classes to body for proper styling
  useEffect(() => {
    // Add both general and mobile-specific classes
    document.body.classList.add("questionnaire-page");
    if (isMobile) {
      document.body.classList.add("mobile-questionnaire");
    }

    return () => {
      document.body.classList.remove("questionnaire-page");
      document.body.classList.remove("mobile-questionnaire");
    };
  }, [isMobile]);

  return (
    <SharedLayout user={user} onLoginClick={onLoginClick}>
      <div
        className="questionnaire-wrapper"
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: "40px 20px 20px",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "2rem",
            width: "100%",
          }}
        >
          <h1
            style={{
              margin: "0 auto",
              fontFamily: '"Times New Roman", Times, serif',
              fontStyle: "italic",
              color: "#3498db",
              fontSize: "2.5rem",
              fontWeight: "700",
              letterSpacing: "1px",
              textTransform: "uppercase",
              padding: "0.5rem 0",
              textAlign: "center",
              display: "block",
            }}
          >
            SIGMUND
          </h1>
          <div
            style={{
              width: "80px",
              height: "3px",
              background: "#3498db",
              margin: "0 auto",
              marginTop: "5px",
            }}
          ></div>
        </div>

        <div
          style={{
            backgroundColor: "white",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginBottom: "2rem",
          }}
        >
          <p
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.8",
              color: "#2c3e50",
              marginBottom: "1.5rem",
            }}
          >
            This questionnaire will take approximately 35-45 minutes and needs
            to be completed in one sitting (the system has a 90 minute timeout
            so you can leave and come back). All questions must be answered as
            the questionnaire won't advance to the next page unless all
            questions are answered on the current page. You can change answers
            by using the "Previous Page" button at the bottom of each page,
            making the desired change(s), and then moving back to where you left
            off using the "Next Page" button.
          </p>
          <p
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.8",
              color: "#2c3e50",
              marginBottom: "1.5rem",
            }}
          >
            If you are using the free Questionnaire-only option, your report
            will be emailed to you within 24 hours. If you are using the paid
            DNA-enhanced version, your Emotional State Report expected delivery
            time is approximately 2-3 weeks (due to courier/post/lab processing
            times). Please get comfortable and begin when ready.
          </p>
        </div>
        <Questionnaire />
      </div>
    </SharedLayout>
  );
};

export default QuestionnaireView;
